import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import BoatFeatureComponent from './components/BoatFeatureComponent';
import BoatInfoComponent from './components/BoatInfoComponent';
// import BoatReviewComponent from './components/BoatReviewComponent';
import BoatRoomComponent from './components/BoatRoomComponent';
// import BoatSimilarBoatComponent from './components/BoatSimilarBoatComponent';
import BoatTripComponent from './components/BoatTripComponent';
import useGetBoat from '../../hooks/useGetBoat';
import useGetBoatFacilities from '../../hooks/useGetBoatFacility';
import useGetBoatPhotos from '../../hooks/useGetBoatPhotos';
import SearchBar from '../Search/components/SearchBar';
import {
  searchInitialState,
  searchReducer,
} from '../../reducers/searchReducer';
import { useEffect, useReducer, useRef, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import {
  IconBed,
  IconChevronDown,
  IconChevronUp,
  IconExclamationCircle,
  IconMinusVertical,
  IconMoon,
  IconScubaMask,
  IconSun,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import SearchOverlay from '../../components/SearchOverlay';
import DrawerMenu from '../Home/components/DrawerMenu';
import useGetSearchResult from '../../hooks/useGetSearchResult';
import { SearchType } from '../../common/constants/searchType';
import type { AvailabilityDetail, RoomType } from '../../gql/graphql';
import { formatDateRange } from '../../common/utilities/dateFormatter';
import useGetBoatRooms from '../../hooks/useGetBoatRooms';
import { IconMapper } from '../../common/helpers/IconMapper';
import { formatPrice } from '../../common/utilities/priceFormatter';
import BoatItineraryDialog from './components/BoatItineraryDialog';
import BoatFacilityDialog from './components/BoatFacilityDialog';
import BoatPhotoDialog from './components/BoatPhotoDialog';
import Footer from '../../components/Footer/Footer';
import { calculateAdjustedPrice } from '../../common/utilities/priceAdjustment';
import useTokenParams from '../../hooks/useTokenParams';
import { groupRoomsAndCalculatePrice } from './utils/roomUtils';
import BoatAction from './components/BoatAction';
import BoatInfoSkeleton from './components/BoatInfoSkeleton';
import BoatTripSkeleton from './components/BoatTripSkeleton';
import BoatRoomSkeleton from './components/BoatRoomSkeleton';
import BoatFeatureSkeleton from './components/BoatFeatureSkeleton';
import type { RoomDetail } from '../../hooks/useCreateBooking';
import { getBoatPageMetadata } from '../../seo/metadata';
import {
  buildJsonLdGraph,
  breadcrumbJsonLd,
  productJsonLd,
} from '../../seo/jsonLd';
import JsonLdScript from '../../seo/JsonLdScript';
import { SITE_ORIGIN } from '../../seo/constants';
import { trackBookingStart } from '../../utils/analytics';
import { getRoomOriginalImageUrl } from '../../common/utilities/cdnImage';
import CdnImage from '../../components/CdnImage';

const Boat = () => {
  const theme = useTheme();
  const isDesktopVersion = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { boatSlug } = useParams();
  const hasTokenParam = new URLSearchParams(location.search).has('token');
  const {
    boatId: initialBoatId,
    tripId: initialTripId,
    diver: initialDiver,
    currentYear: initialCurrentYear,
    searchType: initialSearchType,
    monthId: initialMonthId,
    destination: initialDestination,
    month: initialMonth,
    destinationId: initialDestinationId,
  } = useTokenParams();

  const divers = initialDiver;
  const [tripId, setTripId] = useState(initialTripId);
  const currentYear = initialCurrentYear;

  const [state, dispatch] = useReducer(searchReducer, {
    ...searchInitialState,
    destination: initialDestination,
    month: initialMonth,
    diver: initialDiver,
    currentYear: initialCurrentYear,
    searchType: initialSearchType,
    monthId: initialMonthId,
    destinationId: initialDestinationId,
  });
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);
  const [isShowDrawer, setIsShowDrawer] = useState(false);
  const [showBoatPhotos, setShowBoatPhotos] = useState(false);
  const [showTripSummary, setShowTripSummary] = useState(false);
  const [currentSettedDivers, setCurrentSettedDivers] = useState(0);
  const [showBoatFacilities, setShowBoatFacilities] = useState(false);
  const [showRoomAmenities, setShowRoomAmenities] = useState<RoomType | null>(
    null,
  );
  const [showResetModal, setShowResetModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showItineraryModal, setShowItineraryModal] = useState(false);
  const [selectedItinerary, setSelectedItinerary] =
    useState<AvailabilityDetail | null>(null);
  const { boat, boatLoading } = useGetBoat(
    initialBoatId ?? undefined,
    boatSlug,
  );
  const { boatPhotos, boatPhotosLoading } = useGetBoatPhotos(
    initialBoatId ?? undefined,
    boatSlug,
  );
  const boatId = initialBoatId ?? boat?.boat.boatId;

  const { searchResult, searchResultLoading } = useGetSearchResult(
    boatId ?? 0,
    0,
    divers,
    currentYear,
    SearchType.Boat,
    !!boat?.boat.boatId,
  );
  const boatRoomComponentRef = useRef<HTMLDivElement>(null);
  const boatTripComponentRef = useRef<HTMLDivElement>(null);
  const { boatFacilities, boatFacilitiesLoading } = useGetBoatFacilities(
    boatId ?? 0,
    0,
  );
  const { boatRooms, boatRoomsLoading } = useGetBoatRooms(
    boatId ?? 0,
    tripId ?? 0,
  );
  const [selectedDetail, setSelectedDetail] =
    useState<AvailabilityDetail | null>(null);
  const [selectedRoomIds, setSelectedRoomIds] = useState<RoomDetail[]>([]);
  const [totalPrice, setTotalPrice] = useState(0);

  const pageMetadata = getBoatPageMetadata({
    boatSlug,
    boatName: boat?.boat.name,
    hasToken: hasTokenParam,
    isLoading: boatLoading,
  });
  const {
    title: pageTitle,
    description: pageDescription,
    canonicalUrl,
    robots,
  } = pageMetadata;

  const heroImageUrl =
    boatPhotos?.boatPhotos.find(photo => photo.isHero)?.imageUrl ||
    boatPhotos?.boatPhotos[0]?.imageUrl ||
    '';

  const boatJsonLd =
    canonicalUrl && boat?.boat.name
      ? buildJsonLdGraph([
          productJsonLd({
            name: boat.boat.name,
            url: canonicalUrl,
            description: pageDescription,
            image: heroImageUrl || undefined,
          }),
          breadcrumbJsonLd([
            { name: 'Home', url: `${SITE_ORIGIN}/` },
            { name: boat.boat.name, url: canonicalUrl },
          ]),
        ])
      : null;

  const captureBookingStart = (
    step: 'choose_trip' | 'choose_rooms' | 'reserve',
  ) => {
    trackBookingStart({
      boat_id: boatId,
      boat_slug: boatSlug,
      boat_name: boat?.boat.name,
      trip_id: selectedDetail?.tripId,
      step,
    });
  };

  const scrollToBoatRoomComponent = () => {
    boatRoomComponentRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };
  const scrollToBoatTripComponent = () => {
    boatTripComponentRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  useEffect(() => {
    if (boatPhotos && !boatPhotosLoading) {
      const heroImageUrl =
        boatPhotos.boatPhotos.find(photo => photo.isHero)?.imageUrl || '';
      if (heroImageUrl) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = heroImageUrl;
        document.head.appendChild(link);
      }
    }
  }, [boatPhotos, boatPhotosLoading]);

  useEffect(() => {
    if (selectedDetail && selectedDetail.tripId !== tripId) {
      setTripId(selectedDetail.tripId);
    }
  }, [selectedDetail, tripId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Reset total price to 0
    let newTotalPrice = 0;

    // If there are selected room IDs
    if (selectedRoomIds.length > 0 && boatRooms?.roomTypes) {
      // Calculate the total price
      selectedRoomIds.forEach(roomId => {
        // Find the room with the matching typeId
        const room = boatRooms.roomTypes.find(
          room => room.typeId === roomId.typeId,
        );
        const roomPrice = room?.price ?? 0;
        newTotalPrice += roomPrice;
      });
    }

    // Finally, set the total price
    setTotalPrice(newTotalPrice);
  }, [selectedRoomIds, boatRooms]);

  const handleActionButton = () => {
    if (selectedRoomIds.length > 0) {
      return 'Reserve trip';
    }
    if (selectedDetail) {
      return 'Choose rooms';
    }

    return 'Choose trip';
  };

  const handleActionButtonDesktop = () => {
    if (selectedRoomIds.length > 0) {
      return 'Reserve my trip';
    }
    if (selectedDetail) {
      return 'Choose available rooms';
    }

    return 'Choose your diving trip';
  };

  const handleSelectedText = () => {
    if (totalPrice > 0) {
      return `Total ${formatPrice(calculateAdjustedPrice(totalPrice), {
        currencySymbol: boat?.boat.currency,
      })}`;
    }
    return `No Selected ${selectedDetail ? 'rooms' : 'trip'}`;
  };

  // const calculateServiceFee = (totalPrice: number): number => {
  //   return (totalPrice * 10) / 100;
  // };

  // const calculateTaxes = (serviceFee: number): number => {
  //   return (serviceFee * 7) / 100;
  // };

  const handleResetModal = () => {
    setSelectedDetail(null);
    setShowResetModal(false);
  };

  const handleReserveTrip = () => {
    if (!selectedDetail) {
      captureBookingStart('choose_trip');
      scrollToBoatTripComponent();
    } else if (currentSettedDivers < divers && currentSettedDivers > 0) {
      captureBookingStart('choose_rooms');
      setShowReminderModal(true);
    } else if (currentSettedDivers < divers) {
      captureBookingStart('choose_rooms');
      scrollToBoatRoomComponent();
    } else {
      captureBookingStart('reserve');
      navigate('/payment', {
        state: {
          boatName: boat?.boat.name,
          tripDate: formatDateRange(
            selectedDetail?.embarkationDate ?? '',
            selectedDetail?.disEmbarkationDate ?? '',
          ),
          embarkationDate: selectedDetail?.embarkationDate ?? '',
          routeName: selectedDetail?.routeName,
          numberOfDays: selectedDetail?.numberOfDay,
          numberOfNights: selectedDetail?.numberOfNight,
          numberOfDives: selectedDetail?.numberOfDive,
          tripId: selectedDetail?.tripId,
          boatId: boatId,
          selectedRooms: selectedRoomIds,
          boatRooms: boatRooms,
          currency: boat?.boat.currency ?? '',
        },
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="robots" content={robots} />
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        {heroImageUrl && <meta property="og:image" content={heroImageUrl} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {heroImageUrl && <meta name="twitter:image" content={heroImageUrl} />}
        {boatJsonLd && <JsonLdScript json={boatJsonLd} />}
      </Helmet>
      <SearchBar
        destination={state.destination}
        month={state.month}
        diver={state.diver}
        setIsShowDrawer={setIsShowDrawer}
        setShowSearchOverlay={setShowSearchOverlay}
        dispatch={dispatch}
        currentYear={state.currentYear}
        searchType={state.searchType}
        monthId={state.monthId}
        destinationId={state.destinationId}
        currentStage={state.currentStage}
      />
      <div className="px-6 md:px-40">
        {!boatLoading && !boatPhotosLoading && boat && boatPhotos ? (
          <BoatInfoComponent
            boatName={boat.boat.name}
            rating={4.5}
            heroImageUrl={
              boatPhotos.boatPhotos.find(photo => photo.isHero)?.imageUrl || ''
            }
            imageUrls={boatPhotos.boatPhotos}
            boatFacilities={boatFacilities?.boatFacilities.slice(0, 3) || []}
            boatFacilitiesLoading={boatFacilitiesLoading}
            setShowBoatPhotos={setShowBoatPhotos}
          />
        ) : (
          <BoatInfoSkeleton />
        )}
        <div className="md:flex items-start justify-between gap-12">
          <div>
            {!searchResultLoading && searchResult ? (
              <div ref={boatTripComponentRef}>
                <BoatTripComponent
                  monthId={
                    state.monthId === 0
                      ? new Date().getMonth() + 1
                      : state.monthId
                  }
                  year={state.currentYear}
                  availabilityDetails={
                    searchResult.tripWithCriteria?.[0]?.availabilityDetails ??
                    []
                  }
                  setSelectedDetail={setSelectedDetail}
                  selectedDetail={selectedDetail}
                  setShowResetModal={setShowResetModal}
                  setShowItineraryModal={setShowItineraryModal}
                  setSelectedItinerary={setSelectedItinerary}
                  tripId={tripId ?? 0}
                />
              </div>
            ) : (
              <BoatTripSkeleton />
            )}
            {!boatRoomsLoading && boatRooms ? (
              <div ref={boatRoomComponentRef}>
                <BoatRoomComponent
                  rooms={boatRooms}
                  currency={boat?.boat.currency ?? 'THB'}
                  tripSelected={selectedDetail === null ? false : true}
                  setShowRoomAmenities={setShowRoomAmenities}
                  currentSettedDivers={currentSettedDivers}
                  setCurrentSettedDivers={setCurrentSettedDivers}
                  setSelectedRoomIds={setSelectedRoomIds}
                  selectedRoomIds={selectedRoomIds}
                />
              </div>
            ) : (
              <BoatRoomSkeleton />
            )}
            {!boatFacilitiesLoading && boatFacilities ? (
              <BoatFeatureComponent
                boatFacilities={boatFacilities.boatFacilities}
                setShowBoatFacilities={setShowBoatFacilities}
              />
            ) : (
              <BoatFeatureSkeleton />
            )}
          </div>
          <div className="p-6 mt-12 flex-col max-w-96 w-[27rem] items-start gap-6 border border-neutral-00 rounded-2xl bg-neutral-00 shadow-l hidden md:flex">
            <BoatAction
              selectedDetail={selectedDetail}
              totalPrice={totalPrice}
              boat={boat}
              selectedRoomIds={selectedRoomIds}
              boatRooms={boatRooms}
              divers={divers}
              currentSettedDivers={currentSettedDivers}
            />
            <button
              type="button"
              className="flex px-6 py-4 justify-center items-start self-stretch font-semibold text-neutral-00 rounded-lg bg-gradient-to-r from-brand-santtai-primary to-brand-santtai-primary-darker"
              onClick={() => handleReserveTrip()}
            >
              {handleActionButtonDesktop()}
            </button>
          </div>
        </div>
        {/* <BoatReviewComponent reviews={[]} />
        <BoatSimilarBoatComponent similarBoats={[]} /> */}
        <div className="sticky w-full bottom-0 left-0 bg-white flex py-4 px-4 border-t border-neutral-100 flex-col items-start space-y-2 md:hidden">
          <div className="flex items-center justify-between gap-2 self-stretch">
            <div className="flex flex-col items-start gap-2 flex-grow flex-shrink-0 flex-0">
              {totalPrice > 0 ? (
                <button
                  className="text-base font-semibold leading-normal text-neutral-800 flex items-center gap-2"
                  onClick={() => setShowTripSummary(!showTripSummary)}
                >
                  {handleSelectedText()}
                  {showTripSummary ? (
                    <IconChevronDown size={16} />
                  ) : (
                    <IconChevronUp size={16} />
                  )}
                </button>
              ) : (
                <span className="text-base font-semibold leading-normal text-neutral-800">
                  {handleSelectedText()}
                </span>
              )}
              {tripId !== 0 && selectedDetail && (
                <span>
                  {formatDateRange(
                    selectedDetail?.embarkationDate ?? '',
                    selectedDetail?.disEmbarkationDate ?? '',
                  )}
                </span>
              )}
            </div>
            <button
              onClick={() => handleReserveTrip()}
              className="flex h-11 px-6 py-4 items-center space-x-2 rounded-lg bg-gradient-to-r from-brand-santtai-primary to-brand-santtai-primary-darker text-white"
            >
              <span className="text-base font-semibold leading-normal">
                {handleActionButton()}
              </span>
            </button>
          </div>
        </div>
        <BoatPhotoDialog
          showBoatPhotos={showBoatPhotos}
          setShowBoatPhotos={setShowBoatPhotos}
          isDesktopVersion={isDesktopVersion}
          boatPhotos={boatPhotos}
        />
        <BoatFacilityDialog
          showBoatFacilities={showBoatFacilities}
          setShowBoatFacilities={setShowBoatFacilities}
          isDesktopVersion={isDesktopVersion}
          boatFacilities={boatFacilities}
        />
        <Dialog
          open={showRoomAmenities !== null}
          onClose={() => setShowRoomAmenities(null)}
          fullScreen
          sx={{
            height: '90%',
            top: 'unset',
          }}
          slotProps={{ paper: { sx: { borderRadius: '1.5rem 1.5rem 0 0' } } }}
        >
          <DialogTitle>
            <div className="flex items-center justify-between">
              <IconButton onClick={() => setShowRoomAmenities(null)}>
                <IconX size={16} />
              </IconButton>
              <span className="font-semibold">
                {showRoomAmenities?.typeName ?? ''}
              </span>
              <span></span>
            </div>
          </DialogTitle>
          <DialogContent dividers>
            <div className="flex flex-col items-start gap-6">
              <div className="flex flex-col items-start gap-6 self-stretch">
                <span className="self-stretch font-semibold text-neutral-500">
                  Overview
                </span>
                <div className="flex flex-col items-start content-start gap-4 self-stretch flex-wrap text-neutral-800">
                  <div className="flex items-center gap-2">
                    <IconBed size={20} />
                    <span>{showRoomAmenities?.bedTypeName ?? ''}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <IconUser size={20} />
                    <span>
                      {showRoomAmenities?.occupancy ?? ''} guests/room
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <IconBed size={20} />
                    <span>{showRoomAmenities?.deckLevelName ?? ''}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-start gap-6 self-stretch">
                <span className="self-stretch font-semibold text-neutral-500">
                  Room amenities
                </span>
                {showRoomAmenities?.roomFacilities?.map(facility => (
                  <div className="flex items-center gap-2">
                    {IconMapper(facility.iconName ?? '', 20)}
                    <span>{facility.name}</span>
                  </div>
                )) ?? <p>No facilities to display.</p>}
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <button
              className="flex px-6 py-3 justify-center items-center gap-2 rounded-lg border border-neutral-800"
              onClick={() => setShowRoomAmenities(null)}
            >
              <span className="font-semibold">Close</span>
            </button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={showTripSummary}
          onClose={() => setShowTripSummary(false)}
          fullScreen
          hideBackdrop={true}
          sx={{
            top: 'unset',
            bottom: '89px',
          }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: '1.5rem 1.5rem 0 0',
                boxShadow: 'none',
              },
            },
          }}
        >
          <DialogTitle>
            <div className="flex items-center justify-between">
              <IconButton onClick={() => setShowTripSummary(false)}>
                <IconX size={16} />
              </IconButton>
              <span className="font-semibold">Trip details</span>
              <span></span>
            </div>
          </DialogTitle>
          <DialogContent>
            <div className="flex flex-col items-start gap-6">
              <span className="font-semibold text-2xl pt-6">
                {boat?.boat.name}
              </span>
              <div className="flex items-center gap-4 self-stretch">
                <div className="flex flex-col items-start gap-2 w-36">
                  <span>
                    {formatDateRange(
                      selectedDetail?.embarkationDate ?? '',
                      selectedDetail?.disEmbarkationDate ?? '',
                    )}
                  </span>
                  <div className="flex item-start gap-2">
                    <IconSun size={16} />
                    <span className="text-sm font-normal">
                      {selectedDetail?.numberOfDay}D
                    </span>
                    <IconMinusVertical size={18} />
                    <IconMoon size={16} />
                    <span className="text-sm font-normal">
                      {selectedDetail?.numberOfNight}N
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-start gap-2">
                  <span>{selectedDetail?.routeName}</span>
                  <div className="flex items-center gap-1">
                    <IconScubaMask size={16} />
                    <span className="text-sm font-normal">
                      {selectedDetail?.numberOfDive} dives
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex p-4 flex-col items-start gap-4 self-stretch bg-neutral-20">
                {groupRoomsAndCalculatePrice(selectedRoomIds, boatRooms).map(
                  ({ typeId, totalPrice }) => {
                    const boatRoom = boatRooms?.roomTypes.find(
                      room => room.typeId === typeId,
                    );
                    return (
                      <div className="flex justify-between items-center self-stretch">
                        <div className="flex items-center gap-4">
                          <CdnImage
                            className="h-20 w-20 shrink-0 object-cover"
                            src={getRoomOriginalImageUrl(boatRoom?.roomPhotos)}
                            variant="ROOMGRID"
                            alt="room image"
                          />
                          <div className="flex flex-col items-start gap-2">
                            <span>{boatRoom?.typeName}</span>
                            <div className="flex items-center gap-1">
                              <IconUser size={16} />
                              <span>{boatRoom?.occupancy} guest</span>
                            </div>
                            <span>
                              {/* {formatPrice(boatRoom?.price ?? 0, {
                                currencySymbol: currency,
                              })}{' '} */}
                              {formatPrice(
                                calculateAdjustedPrice(boatRoom?.price ?? 0),
                                { currencySymbol: boat?.boat.currency },
                              )}{' '}
                              per guest
                            </span>
                          </div>
                        </div>
                        <span>
                          {formatPrice(calculateAdjustedPrice(totalPrice), {
                            currencySymbol: boat?.boat.currency,
                          })}
                        </span>
                      </div>
                    );
                  },
                )}
                {/* <div className="w-full">
                  <Divider className="bg-neutral-100" />
                </div>
                <div className="flex justify-between items-center self-stretch">
                  <span>Santtai service fee</span>
                  <span>{formatPrice(calculateServiceFee(totalPrice))}</span>
                </div>
                <div className="flex justify-between items-center self-stretch">
                  <span>Taxes</span>
                  <span>
                    {formatPrice(
                      calculateTaxes(calculateServiceFee(totalPrice)),
                    )}
                  </span>
                </div> */}
                <div className="w-full">
                  <Divider className="bg-neutral-100" />
                </div>
                <div className="flex justify-between items-center self-stretch">
                  <span>Total</span>
                  <span>
                    {/* {formatPrice(
                      (calculateServiceFee(totalPrice) * 7) / 100 +
                        totalPrice +
                        calculateServiceFee(totalPrice),
                    )} */}

                    {formatPrice(calculateAdjustedPrice(totalPrice), {
                      currencySymbol: boat?.boat.currency,
                    })}
                  </span>
                </div>
                <div className="w-full">
                  <Divider className="bg-neutral-100" />
                </div>
                <div className="text-green-700">
                  <span className="font-semibold">Book now, pay later.</span>
                  <span className="ml-2">
                    We'll hold your spot, free of charge. No credit card needed!
                  </span>
                </div>
              </div>
              {divers > currentSettedDivers && (
                <div className="flex items-start gap-4 self-stretch">
                  <IconExclamationCircle size={20} />
                  <span>
                    There remains{' '}
                    <b>
                      {divers - currentSettedDivers} guests without the
                      allocated room
                    </b>
                  </span>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <Dialog open={showResetModal}>
        <DialogTitle>
          <div className="flex items-end justify-end w-full">
            <IconX onClick={() => setShowResetModal(false)} size={20} />
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="flex flex-col items-start gap-12">
            <div className="flex flex-col items-start gap-4 self-stretch">
              <div className="text-xl font-semibold text-neutral-800">
                Change your selected trip?
              </div>
              <div className="text-neutral-500">
                Upon confirmation, your currently selected rooms will be
                cleared, allowing you to begin selecting new rooms for your
                upcoming trip.
              </div>
            </div>
            <div className="flex flex-col justify-center items-end gap-4 self-stretch">
              <button
                type="button"
                className="flex h-11 py-3 px-6 justify-center items-center gap-2 self-stretch rounded-lg bg-neutral-800 font-semibold text-neutral-00"
                onClick={() => handleResetModal()}
              >
                Confirm
              </button>
              <button
                type="button"
                className="flex h-11 py-3 px-6 justify-center items-center gap-2 self-stretch rounded-lg border border-neutral-800 font-semibold text-neutral-800"
                onClick={() => setShowResetModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showReminderModal}>
        <DialogTitle>
          <div className="flex items-end justify-end w-full">
            <IconX onClick={() => setShowReminderModal(false)} size={20} />
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="flex flex-col items-start gap-12">
            <div className="flex flex-col items-start gap-4 self-stretch">
              <div className="text-xl font-semibold text-neutral-800">
                Reserve a trip for {currentSettedDivers} guest
                {currentSettedDivers !== 1 ? 's' : ''}?
              </div>
              <div className="text-neutral-500">
                Your search was for {divers} guest{divers !== 1 ? 's' : ''}, but
                the room selected accommodates only {currentSettedDivers} guest
                {currentSettedDivers !== 1 ? 's' : ''}.
                {divers - currentSettedDivers} guest
                {divers - currentSettedDivers !== 1 ? 's' : ''} will not have an
                assigned room. Are you sure to continue?
              </div>
            </div>
            <div className="flex flex-col justify-center items-end gap-4 self-stretch">
              <button
                type="button"
                className="flex h-11 py-3 px-6 justify-center items-center gap-2 self-stretch rounded-lg bg-neutral-800 font-semibold text-neutral-00"
                onClick={() =>
                  navigate('/payment', {
                    state: {
                      boatName: boat?.boat.name,
                      tripDate: formatDateRange(
                        selectedDetail?.embarkationDate ?? '',
                        selectedDetail?.disEmbarkationDate ?? '',
                      ),
                      embarkationDate: selectedDetail?.embarkationDate ?? '',
                      routeName: selectedDetail?.routeName,
                      numberOfDays: selectedDetail?.numberOfDay,
                      numberOfNights: selectedDetail?.numberOfNight,
                      numberOfDives: selectedDetail?.numberOfDive,
                      tripId: selectedDetail?.tripId,
                      boatId: boatId,
                      selectedRooms: selectedRoomIds,
                      boatRooms: boatRooms,
                      currency: boat?.boat.currency ?? '',
                    },
                  })
                }
              >
                Reserve
              </button>
              <button
                type="button"
                className="flex h-11 py-3 px-6 justify-center items-center gap-2 self-stretch rounded-lg border border-neutral-800 font-semibold text-neutral-800"
                onClick={() => setShowReminderModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <BoatItineraryDialog
        showItineraryModal={showItineraryModal}
        setShowItineraryModal={setShowItineraryModal}
        selectedItinerary={selectedItinerary}
        boatName={boat?.boat.name ?? ''}
        tripId={tripId ?? 0}
        isDesktopVersion={isDesktopVersion}
        setSelectedDetail={setSelectedDetail}
        selectedDetail={selectedDetail}
        scrollToBoatRoomComponent={scrollToBoatRoomComponent}
      />
      <SearchOverlay
        showSearchOverlay={showSearchOverlay}
        setShowSearchOverlay={setShowSearchOverlay}
        dispatch={dispatch}
        destination={state.destination}
        month={state.month}
        diver={state.diver}
        currentYear={state.currentYear}
        searchType={state.searchType}
        destinationId={state.destinationId}
        monthId={state.monthId}
      />
      <DrawerMenu
        isShowDrawer={isShowDrawer}
        setIsShowDrawer={setIsShowDrawer}
      />
      <Footer />
    </>
  );
};

export default Boat;
